import Phaser from 'phaser';
import { getCurrentTheme, getConfig, showMarqueeMessage } from '../Utils.js';
import BattleStateManager from '../game/BattleStateManager.js';
import sessionStateManager from '../game/SessionStateManager.js';

/**
 * PreBattleScene - Battlefield showcase and asset loading scene
 * 
 * This scene displays the selected battlefield for 15 seconds while loading
 * all required assets for the upcoming battle. It shows battlefield details,
 * owner information, loot statistics, and a call-to-action for renting/owning
 * the battlefield.
 * 
 * @class PreBattleScene
 */
export class PreBattleScene extends Phaser.Scene {
  constructor() {
    super({ key: 'PreBattleScene' });
    this.displayDuration = 15000; // 15 seconds
    this.startTime = null;
    this.loadingProgress = 0;
    this.assetsLoaded = false;
    this.remainingSeconds = this.displayDuration / 1000;
  }

  init(data) {
    // Get battlefield data from scene data or BattleStateManager
    this.battlefield = data?.battlefield || BattleStateManager.getBattlefield();
    this.playerCount = data?.playerCount || 0;
    this.teamCount = data?.teamCount || 1;
    
    // Get music state from JoinScene
    this.musicState = data?.musicState || null;
    
    // Battlefield instance data (will be fetched in create)
    this.battlefieldInstance = null;
    this.purchaseButton = null;
    this.purchaseText = null;

    // Reset transient state so the scene behaves consistently on each run
    this.startTime = null;
    this.loadingProgress = 0;
    this.assetsLoaded = false;
    this.remainingSeconds = this.displayDuration / 1000;
    this.loadingText = null;
    this.progressBar = null;
    this.progressBg = null;
    this.currentLobbyMusic = null;
    this.currentLobbyMusicKey = null;
    
    console.log('🎬 PreBattleScene initialized:', {
      battlefield: this.battlefield?.name,
      playerCount: this.playerCount,
      teamCount: this.teamCount,
      hasMusicState: !!this.musicState
    });
  }

  preload() {
    // Set image load type for better compatibility with external URLs
    // Try different load types if one fails
    this.load.imageLoadType = "HTMLImageElement";
    //console.log('🎮 PreBattleScene: imageLoadType set to HTMLImageElement');
    
    // Get theme for asset loading
    const theme = getCurrentTheme();
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    let s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');

    // Remove trailing slash from s3BasePath to avoid double slashes
    if (s3BasePath && s3BasePath.endsWith('/')) {
      s3BasePath = s3BasePath.slice(0, -1);
    }

    const useS3 = !!s3BasePath;

    // Load player textures from theme if not already loaded
    if (theme && theme.players && Array.isArray(theme.players)) {
      console.log(`🎯 PreBattleScene: Loading ${theme.players.length} player textures from theme`);
      
      theme.players.forEach(playerAsset => {
        const filename = typeof playerAsset === 'string' ? playerAsset : (playerAsset && (playerAsset.image || playerAsset.asset || ''));
        const safeFilename = String(filename || '');
        const assetKey = safeFilename.replace(/\.[^/.]+$/, ''); // Remove extension for key

        // Check if texture is already loaded
        if (!this.textures.exists(assetKey)) {
          // Use S3 path for custom themes, local path for default theme
          const assetPath = useS3
            ? `${s3BasePath}/players/${safeFilename}`
            : `assets/players/${safeFilename}`;

          //console.log(`📥 PreBattleScene: Loading player texture: ${assetKey} from ${assetPath}`);
          this.load.image(assetKey, assetPath);
        }
      });
    } else {
      console.warn('⚠️ PreBattleScene: No player textures in theme, loading fallback textures');
      
      // Load fallback player textures
      const fallbackPlayers = [
        'easy1', 'easy2', 'easy3', 'easy4', 'easy5', 'easy6',
        'elite1', 'elite2', 'elite3', 'elite4', 'elite5', 'elite6', 'elite7', 'elite8', 'elite9',
        'expert1', 'expert2', 'expert3',
        'hard1', 'hard2', 'hard3', 'hard4',
        'normal1', 'normal2', 'normal3', 'normal4'
      ];
      
      fallbackPlayers.forEach(texture => {
        if (!this.textures.exists(texture)) {
          const assetPath = `assets/players/${texture}.png`;
          console.log(`📥 PreBattleScene: Loading fallback player texture: ${texture} from ${assetPath}`);
          this.load.image(texture, assetPath);
        }
      });
    }

    // Load battlefield background image using the same texture key format as GameScene
    if (this.battlefield && this.battlefield.image) {
      // Use the same texture key format as GameScene (just the image filename)
      const textureKey = this.battlefield.image;
      console.log(`🎨 PreBattleScene: Using battlefield texture key: ${textureKey}`);

      // Check if texture is already loaded from JoinScene
      if (!this.textures.exists(textureKey)) {
        console.log(`📸 PreBattleScene: Loading battlefield texture: ${textureKey}`);

        let assetPath;

        if (useS3) {
          // Use S3 path
          const imagePath = this.battlefield.image.startsWith('battlefields/')
            ? this.battlefield.image
            : `battlefields/${this.battlefield.image}`;
          assetPath = `${s3BasePath}/${imagePath}`;
        } else {
          // Use local path
          assetPath = this.battlefield.image.startsWith('battlefields/')
            ? `assets/${this.battlefield.image}`
            : `assets/battlefields/${this.battlefield.image}`;
        }

        this.load.image(textureKey, assetPath);
        console.log(`📸 PreBattleScene: Loading battlefield texture from: ${assetPath}`);
      } else {
        console.log(`✅ PreBattleScene: Battlefield texture already loaded: ${textureKey}`);
      }
    } else {
      console.log('⚠️ PreBattleScene: No battlefield data available');
    }
    
    // Preload next battle music for smooth transitions
    this.preloadNextBattleMusic(theme);
    
    // Note: Player textures are now loaded individually in the player loop below
    // This ensures we use the correct URLs for each player

    // Load textures for actual players that will be spawned
    const playerList = BattleStateManager.getPlayerList();
    console.log(`🎮 PreBattleScene: Loading textures for ${playerList.length} players`);
    
    playerList.forEach((playerData, index) => {
      const textureKey = `player_${playerData.hero?.id || playerData.wallet || playerData.id}`;

      //Example playerData:
      /*
      {
          "id": "ai_45bcfa7d-3c63-4d5a-beab-663c048c6cc9",
          "name": "Sassydingo",
          "wallet": "ai_wallet_45bcfa7d-3c63-4d5a-beab-663c048c6cc9",
          "userId": 1,
          "heroId": "45bcfa7d-3c63-4d5a-beab-663c048c6cc9",
          "isAI": true,
          "isJoined": false,
          "teamId": 0,
          "hero": {
              "id": "45bcfa7d-3c63-4d5a-beab-663c048c6cc9",
              "name": "Sassydingo",
              "image_url": "https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/elite2.png",
              "level": 1,
              "stats": {
                  "health": 107,
                  "attack": 60,
                  "defense": 55,
                  "speed": 85
              },
              "battle_frequency": 6,
              "inventory": [
                  {
                      "item_id": "sword_basic",
                      "item_type": "weapon",
                      "slot_number": 0,
                      "is_equipped": 1,
                      "equipment_slot": "mainhand",
                      "quantity": 1,
                      "durability": 100,
                      "max_durability": 100
                  }
              ],
              "strategies": {
                  "aggression": 7,
                  "looting": 23,
                  "positioning": 43,
                  "melee_ranged": 82,
                  "item_use": 100
              }
          },
          "spawnPosition": null
      }
      */
      //loader.imageLoadType: "HTMLImageElement"

      // Check if texture is already loaded
      if (!this.textures.exists(textureKey)) {
        //console.log(playerData);
        
        // Check if player has a valid hero image_url (for AI players from database)
        if (playerData.hero && playerData.hero.image_url && playerData.hero.image_url !== '⚔️') {
          const imageUrl = playerData.hero.image_url;
          //console.log(`📥 PreBattleScene: Loading player texture for ${playerData.name}: ${textureKey} from ${imageUrl}`);
          
          // Test if the URL is accessible
          fetch(imageUrl, { method: 'HEAD' })
            .then(response => {
              if (response.ok) {
                //console.log(`✅ URL accessible: ${imageUrl}`);
              } else {
                console.warn(`⚠️ URL not accessible (${response.status}): ${imageUrl}`);
              }
            })
            .catch(error => {
              console.error(`❌ URL fetch error: ${imageUrl}`, error);
            });
          
          this.load.image(textureKey, imageUrl);
        } else {
          // For random players or players without valid image_url, use fallback texture
          // Random players will use fallback textures that are already loaded in the theme section
          console.log(`📥 PreBattleScene: Random player ${playerData.name} will use fallback texture (no custom texture needed)`);
          // Don't load a texture here - random players will use the fallback textures already loaded
        }
      }
    });

    // Note: Phaser automatically starts loading when preload() completes
    // No need to manually call this.load.start()
    
    // Set up loading progress tracking
    this.load.on('progress', (progress) => {
      this.loadingProgress = Math.floor(progress * 100);
      BattleStateManager.setLoadingProgress(this.loadingProgress);
    });
    
    this.load.on('complete', () => {
      //console.log('✅ All assets loaded successfully');
      //console.log('📊 Loaded textures:', Object.keys(this.textures.list));
      this.assetsLoaded = true;
      BattleStateManager.setAssetsLoaded(true);
    });
    
    this.load.on('loaderror', (file) => {
      console.error(`❌ Failed to load asset: ${file.key}`, {
        url: file.url,
        type: file.type,
        error: file.error
      });
    });
  }

  preloadNextBattleMusic(theme) {
    if (!theme || !theme.music || !theme.music.battle) {
      console.log('🎵 No battle music data available for preloading');
      return;
    }

    console.log('🎵 Preloading next battle music...');
    
    const battleMusic = theme.music.battle;
    if (!Array.isArray(battleMusic) || battleMusic.length === 0) {
      console.log('🎵 No battle music tracks available');
      return;
    }

    // Get a random song to preload (not necessarily the next one, but one that might be played soon)
    const randomMusic = battleMusic[Math.floor(Math.random() * battleMusic.length)];
    const key = randomMusic.key;
    
    // Check if already loaded
    if (this.cache.audio && this.cache.audio.exists(key)) {
      console.log('🎵 Next battle music already loaded:', key);
      return;
    }

    // Load the music
    const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
    const s3BasePath = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}`;
    const musicPath = `${s3BasePath}/music/${key}`;
    
    console.log('🎵 Preloading next battle music:', key, 'from', musicPath);
    this.load.audio(key, musicPath);
  }


  continueMusicFromJoinScene() {
    if (!this.musicState) {
      console.log('🎵 No music state from JoinScene to continue');
      return;
    }

    console.log('🎵 Continuing lobby music from JoinScene...');
    
    // Transfer music state from JoinScene
    this.currentLobbyMusic = this.musicState.currentMusic;
    this.currentLobbyMusicKey = this.musicState.currentMusicKey;
    this.lobbyMusicQueue = this.musicState.musicQueue;
    this.lobbyMusicManifest = this.musicState.musicManifest;
    
    // If there's currently playing music, continue it
    if (this.currentLobbyMusic && this.currentLobbyMusic.isPlaying) {
      console.log('🎵 Lobby music continuing from JoinScene:', this.currentLobbyMusicKey);
    } else {
      console.log('🎵 No music currently playing, will start when ready');
    }
  }

  setupFocusHandling() {
    
    // Prevent music from pausing when window loses focus
    this.focusHandler = () => {
      if (this.currentLobbyMusic && this.currentLobbyMusic.isPaused) {
        //console.log('🎵 Resuming lobby music after window focus');
        this.currentLobbyMusic.resume();
      }
    };

    this.blurHandler = () => {
      // Don't pause music on blur - let it continue playing
      //console.log('🎵 Window lost focus, but keeping music playing');
    };

    this.visibilityChangeHandler = () => {
      if (document.hidden) {
        //console.log('🎵 Tab hidden, but keeping music playing');
      } else {
        //console.log('🎵 Tab visible again, ensuring music is playing');
        if (this.currentLobbyMusic && this.currentLobbyMusic.isPaused) {
          this.currentLobbyMusic.resume();
        }
      }
    };

    // Add event listeners
    window.addEventListener('focus', this.focusHandler);
    window.addEventListener('blur', this.blurHandler);
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  async create() {
    const { width, height } = this.cameras.main;
    
    // Update state to 'pre-battle'
    sessionStateManager.updateState('pre-battle');
    
    // Add focus/blur event listeners to prevent music pausing
    //this.setupFocusHandling();
    
    // Continue lobby music from JoinScene
    this.continueMusicFromJoinScene();
    
    // Create dark background
    this.background = this.add.rectangle(width / 2, height / 2, width, height, 0x0F172A);
    
    // Add battlefield background image if loaded
    let battlefieldTextureKey = this.battlefield ? this.battlefield.image : 'default-battlefield';
    console.log(`🎨 PreBattleScene: Creating battlefield image with key: ${battlefieldTextureKey}`);
    console.log(`🎨 PreBattleScene: Texture exists in cache: ${this.textures.exists(battlefieldTextureKey)}`);

    //If the texture does not exist, log it and use a fallback battlefield from the local assets
    if (!this.textures.exists(battlefieldTextureKey)) {
      console.log(`🎨 PreBattleScene: Texture does not exist: ${battlefieldTextureKey}`);
      console.log(`🎨 PreBattleScene: Using fallback battlefield from local assets`);
      battlefieldTextureKey = 'default-battlefield';
      this.battlefield.image = 'default-battlefield';
    }

    
    if (this.textures.exists(battlefieldTextureKey)) {
      this.battlefieldImage = this.add.image(width / 2, height / 2, battlefieldTextureKey);
      
      // Scale to cover the screen while maintaining aspect ratio
      const scaleX = width / this.battlefieldImage.width;
      const scaleY = height / this.battlefieldImage.height;
      const scale = Math.max(scaleX, scaleY);
      this.battlefieldImage.setScale(scale);
      this.battlefieldImage.setDepth(0);
      
      // Add dark overlay for better text readability
      this.overlay = this.add.rectangle(width / 2, height / 2, width, height, 0x000000);
      this.overlay.setAlpha(0.6);
      this.overlay.setDepth(1);
    } 
    
    // Create main content container
    this.contentContainer = this.add.container(width / 2, height / 2).setDepth(10);
    
    // Fetch battlefield instance from server
    await this.fetchBattlefieldInstance();
    
    // Create battlefield showcase UI
    this.createBattlefieldShowcase(width, height);
    
    // Create loading progress indicator
    //this.createLoadingIndicator(width, height);
    
    // Start the display timer (delayed by 1 second for fade-in)
    this.startTime = this.time.now + 1000; // Add 1 second delay for fade-in
    
    // Fade in animation
    this.contentContainer.setAlpha(0);
    this.tweens.add({
      targets: this.contentContainer,
      alpha: 1,
      duration: 800,
      ease: 'Power2'
    });
  }

  async fetchBattlefieldInstance() {
    try {
      const theme = getCurrentTheme();
      if (!theme || !this.battlefield) {
        console.warn('⚠️ PreBattleScene: No theme or battlefield data available');
        return;
      }

      // Check if there's a manually selected battlefield instance ID
      const battlefieldInstanceId = BattleStateManager.getBattlefieldInstanceId();
      
      if (battlefieldInstanceId) {
        // Fetch full battlefield details including owner name
        try {
          const authToken = typeof window !== 'undefined' ? localStorage.getItem('jwt_token') : null;
          const response = await fetch(`/api/battlefields/${battlefieldInstanceId}`, {
            headers: {
              'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key',
              ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {})
            }
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.data) {
              this.battlefieldInstance = data.data;
              console.log('✅ PreBattleScene: Battlefield instance fetched with owner info:', {
                id: this.battlefieldInstance.id,
                ownerUserId: this.battlefieldInstance.ownerUserId,
                ownerName: this.battlefieldInstance.ownerName
              });
              return;
            }
          }
        } catch (error) {
          console.warn('⚠️ PreBattleScene: Failed to fetch battlefield instance details, falling back to available endpoint:', error);
        }
      }

      // Fallback to available endpoint for random/unowned battlefields
      const battlefieldId = this.battlefield.id || this.battlefield.battlefield_id || this.battlefield.battlefieldId;
      const themeId = theme.id;

      if (!battlefieldId || !themeId) {
        console.warn('⚠️ PreBattleScene: Missing battlefield ID or theme ID');
        return;
      }

      const response = await fetch(`/api/battlefields/available/${themeId}/${battlefieldId}`, {
        headers: {
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        }
      });

      if (!response.ok) {
        console.warn('⚠️ PreBattleScene: Failed to fetch battlefield instance');
        return;
      }

      const data = await response.json();
      if (data.success && data.data) {
        this.battlefieldInstance = data.data;
        console.log('✅ PreBattleScene: Battlefield instance fetched:', {
          id: this.battlefieldInstance.id,
          isForSale: this.battlefieldInstance.isForSale,
          ownerUserId: this.battlefieldInstance.ownerUserId,
          price: this.battlefieldInstance.priceSoulwars
        });

        // Show marquee message if already purchased
        if (this.battlefieldInstance.ownerUserId && !this.battlefieldInstance.isForSale) {
          const ownerName = this.battlefieldInstance.ownerName || 'another player';
          showMarqueeMessage(
            `🏰 This battlefield is owned by ${ownerName}`,
            { color: '#8B5CF6' },
            'medium'
          );
        }
      }
    } catch (error) {
      console.error('❌ PreBattleScene: Error fetching battlefield instance:', error);
    }
  }

  createBattlefieldShowcase(width, height) {
    // Main showcase container
    const showcaseWidth = Math.min(850, width * 0.95);
    const showcaseHeight = Math.min(600, height * 0.7);
    
    //panelBg.setStrokeStyle(2, 0x4B5563);
    //panelBg.setAlpha(0.8);
    // Background panel
    const panelBg = this.add.rectangle(0, 0, showcaseWidth, showcaseHeight, 0x000000);
    panelBg.setStrokeStyle(2, 0x4B5563);
    panelBg.setAlpha(0.8);
    this.contentContainer.add(panelBg);
    
    // Battlefield title - use custom name if available, otherwise template name
    const battlefieldName = this.battlefieldInstance?.customName || this.battlefield?.name || 'Unknown Battlefield';
    const title = this.add.text(0, -showcaseHeight / 2 + 40, battlefieldName, {
      fontSize: '36px',
      color: '#F3F300',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    }).setOrigin(0.5);
    this.contentContainer.add(title);
    
    // Battlefield description
    const description = this.add.text(0, -showcaseHeight / 2 + 90, this.battlefield?.description || 'A mysterious battlefield awaits...', {
      fontSize: '18px',
      color: '#E2E8F0',
      fontFamily: 'Arial, sans-serif',
      align: 'center',
      wordWrap: { width: showcaseWidth - 60 }
    }).setOrigin(0.5);
    this.contentContainer.add(description);
    
    // Battle info section
    const battleInfoY = -showcaseHeight / 2 + 180;
    
    // Players count
    const playersText = this.add.text(0, battleInfoY, `👥 ${this.playerCount} Players`, {
      fontSize: '30px',
      color: '#10B981',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    this.contentContainer.add(playersText);
    
    // Teams count
    /*const teamsText = this.add.text(showcaseWidth / 2 - 40, battleInfoY, `🏆 ${this.teamCount} Teams`, {
      fontSize: '20px',
      color: '#F59E0B',
      fontFamily: 'Arial, sans-serif',
      fontStyle: 'bold'
    }).setOrigin(1, 0.5);
    this.contentContainer.add(teamsText);*/
    
    // Dynamic content section - owner, loot stats, and Loot Team efficiencies
    let currentY = battleInfoY + 60; // Start below players count
    
    // Owner section - only show if battlefield instance has an owner
    if (this.battlefieldInstance && this.battlefieldInstance.ownerUserId) {
      const ownerName = this.battlefieldInstance.ownerName || `Owner ID ${this.battlefieldInstance.ownerUserId}`;
      const ownerText = this.add.text(0, currentY, `Battlefield Owner: 👑 ${ownerName}`, {
        fontSize: '22px',
        color: '#8B5CF6',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      this.contentContainer.add(ownerText);
      currentY = ownerText.y + 35;

      // Description text about owners
      const bfDescriptionText = this.add.text(0, currentY, `Battlefields owners collect loot and dropped tokens from eliminated players.`, {
        fontSize: '18px',
        color: '#FFD700', //gold
        align: 'center',
        outline: '#ffffff',
        outlineThickness: 2,
        dropShadow: true,
        dropShadowColor: '#000000',
        dropShadowBlur: 10,
        dropShadowOffsetX: 0,
        dropShadowOffsetY: 0,
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        wordWrap: { width: showcaseWidth - 60 }
      }).setOrigin(0.5);
      this.contentContainer.add(bfDescriptionText);
      currentY = bfDescriptionText.y + 40;
      
      // Loot section - only show if we have loot data
      // TODO: Add totalLootHarvested to battlefieldInstance response when available
      if (this.battlefieldInstance.totalLootHarvested !== undefined && this.battlefieldInstance.totalLootHarvested !== null) {
        const lootText = this.add.text(0, currentY, `💰 Battle Loot Harvested: ${this.battlefieldInstance.totalLootHarvested.toLocaleString()} $SOULWARS`, {
          fontSize: '28px',
          color: '#F59E0B',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold'
        }).setOrigin(0.5);
        this.contentContainer.add(lootText);
        currentY = lootText.y + 50;
      }
    }
    
    // Loot Team Efficiency section - always show if instance data is available
    if (this.battlefieldInstance) {
      currentY = currentY - 20; // Add spacing before efficiency section
      
      const efficiencyTitle = this.add.text(0, currentY, 'Loot Team Efficiency', {
        fontSize: '24px',
        color: '#FFFFFF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0.5);
      this.contentContainer.add(efficiencyTitle);
      currentY = efficiencyTitle.y + 45; // More space so title doesn't overlap cards
      
      // Format efficiency value helper - whole numbers only
      const formatEfficiency = (value) => {
        return typeof value === 'number' ? Math.round(value) + '%' : '0%';
      };
      
      // Efficiency stats - display in a 2x2 grid
      const efficiencyStats = [
        { type: 'token', label: 'Tokens', value: this.battlefieldInstance.lootTeamTokenEfficiency, icon: '💰' },
        { type: 'item', label: 'Items', value: this.battlefieldInstance.lootTeamItemEfficiency, icon: '📦' },
        { type: 'potion', label: 'Potions', value: this.battlefieldInstance.lootTeamPotionEfficiency, icon: '🧪' },
        { type: 'gem', label: 'Gems', value: this.battlefieldInstance.lootTeamGemEfficiency, icon: '💎' }
      ];
      
      const statWidth = (showcaseWidth - 60) / 2;
      const statHeight = 60; // Increased height for larger fonts
      const statSpacing = 15;
      const startX = -showcaseWidth / 2 + 30 + statWidth / 2;
      
      efficiencyStats.forEach((stat, index) => {
        const row = Math.floor(index / 2);
        const col = index % 2;
        const statX = startX + col * (statWidth + statSpacing);
        const statY = currentY + row * (statHeight + statSpacing);
        
        // Background rectangle for each stat
        const statBg = this.add.rectangle(statX, statY, statWidth, statHeight, 0x1F2937);
        statBg.setStrokeStyle(1, 0x374151);
        statBg.setAlpha(0.8);
        this.contentContainer.add(statBg);
        
        // Icon - larger for mobile
        const statIcon = this.add.text(statX - statWidth / 2 + 25, statY, stat.icon, {
          fontSize: '32px',
          fontFamily: 'Arial, sans-serif'
        }).setOrigin(0, 0.5);
        this.contentContainer.add(statIcon);
        
        // Label - larger font for mobile
        const statLabel = this.add.text(statX - statWidth / 2 + 70, statY - 2, stat.label, {
          fontSize: '20px',
          color: '#FFFFFF',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 1
        }).setOrigin(0, 0.5);
        this.contentContainer.add(statLabel);
        
        // Efficiency value - larger font for mobile
        const statValue = this.add.text(statX + 60, statY, formatEfficiency(stat.value), {
          fontSize: '28px',
          color: '#10B981',
          fontFamily: 'Arial, sans-serif',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 2
        }).setOrigin(0.5, 0.5);
        this.contentContainer.add(statValue);
      });
      
      // Update currentY to position after efficiency grid
      currentY = currentY + (2 * (statHeight + statSpacing)) - statSpacing + 20;
    }
    
    // Call to action section - Show purchase UI if battlefield is for sale
    // Position CTA dynamically based on content above, or use fixed position if no instance data
    let ctaY = this.battlefieldInstance ? Math.min(currentY + 30, showcaseHeight / 2 - 80) : showcaseHeight / 2 - 80;
    
    if (this.battlefieldInstance && this.battlefieldInstance.isForSale && !this.battlefieldInstance.ownerUserId) {
      ctaY = ctaY + 20;
      // Available to Purchase - enlarged to fit all 3 lines
      const price = this.battlefieldInstance.priceSoulwars || 1000000;
      const ctaHeight = 150; // Increased height to fit all content
      const ctaBg = this.add.rectangle(0, ctaY, showcaseWidth - 40, ctaHeight, 0x10B981);
      ctaBg.setStrokeStyle(2, 0x059669);
      this.contentContainer.add(ctaBg);
      
      const ctaText = this.add.text(0, ctaY - 35, `💰 Soon Available to Purchase`, {
        fontSize: '32px',
        color: '#FFFFFF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0.5);
      this.contentContainer.add(ctaText);
      
      // Price text - 50% larger (from 20px to 30px)
      const priceText = this.add.text(0, ctaY + 15, `${price.toLocaleString()} $SOULWARS`, {
        fontSize: '36px',
        color: '#F3F300',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2
      }).setOrigin(0.5);
      this.contentContainer.add(priceText);
      
      // Buy hint text - inside the green area
      /*const buyHintText = this.add.text(0, ctaY + 60, 'Type "buy" in chat to purchase', {
        fontSize: '24px',
        color: '#E2E8F0',
        fontFamily: 'Arial, sans-serif',
        stroke: '#000000',
        strokeThickness: 1
      }).setOrigin(0.5);
      this.contentContainer.add(buyHintText);*/
      
      // Store references for purchase handling
      this.purchaseButton = ctaBg;
      this.purchaseText = ctaText;
      
      // Make CTA interactive
      ctaBg.setInteractive({ useHandCursor: true });
      ctaBg.on('pointerdown', () => {
        this.handleBattlefieldPurchase();
      });
      
      ctaBg.on('pointerover', () => {
        ctaBg.setFillStyle(0x059669);
        ctaBg.setScale(1.02);
      });
      
      ctaBg.on('pointerout', () => {
        ctaBg.setFillStyle(0x10B981);
        ctaBg.setScale(1);
      });
    } else {
      // Default CTA (not for sale)
      const ctaBg = this.add.rectangle(0, ctaY, showcaseWidth - 40, 60, 0x059669);
      ctaBg.setStrokeStyle(2, 0x10B981);
      this.contentContainer.add(ctaBg);
      
      const ctaText = this.add.text(0, ctaY, '🏰 Buy your own Battlefield now at SoulWars.gg!', {
        fontSize: '24px',
        color: '#FFFFFF',
        fontFamily: 'Arial, sans-serif',
        fontStyle: 'bold'
      }).setOrigin(0.5);
      this.contentContainer.add(ctaText);
      
      // Make CTA interactive
      ctaBg.setInteractive({ useHandCursor: true });
      ctaBg.on('pointerdown', () => {
        console.log('🏰 Battlefield ownership clicked');
        // TODO: Navigate to shop page
      });
      
      ctaBg.on('pointerover', () => {
        ctaBg.setFillStyle(0x10B981);
        ctaBg.setScale(1.02);
      });
      
      ctaBg.on('pointerout', () => {
        ctaBg.setFillStyle(0x059669);
        ctaBg.setScale(1);
      });
    }
    
    // Create countdown progress bar at the bottom
    this.createProgressBar(showcaseWidth, showcaseHeight);
    
    // Make progress bar clickable to skip to 1 second
    if (this.progressBarBg) {
      this.progressBarBg.setInteractive({ useHandCursor: true });
      this.progressBarBg.on('pointerdown', () => {
        console.log('⏰ Progress bar clicked - skipping to 1 second remaining');
        // Set the start time so that only 1 second remains (accounting for fade-in delay)
        this.startTime = this.time.now - (this.displayDuration - 1000) + 1000;
      });
    }
  }
  
  createProgressBar(containerWidth, containerHeight) {
    const barWidth = containerWidth * 0.8;
    const barHeight = 20;
    const barY = containerHeight / 2 - 30;
    
    // Background bar (full width, dark)
    this.progressBarBg = this.add.rectangle(0, barY, barWidth, barHeight, 0x1E293B);
    this.progressBarBg.setStrokeStyle(2, 0xFFFFFF);
    this.progressBarBg.setAlpha(0); // Start invisible
    this.contentContainer.add(this.progressBarBg);
    
    // Progress bar fill (starts full width, shrinks as time passes)
    this.progressBarFill = this.add.rectangle(-barWidth / 2, barY, barWidth, barHeight - 4, 0xF3F300);
    this.progressBarFill.setOrigin(0, 0.5);
    this.progressBarFill.setAlpha(0); // Start invisible
    this.contentContainer.add(this.progressBarFill);
    
    // Add glow effect to progress bar
    const progressGlow = this.add.rectangle(0, barY, barWidth, barHeight + 4, 0xF3F300);
    progressGlow.setAlpha(0); // Start invisible
    progressGlow.setBlendMode(Phaser.BlendModes.ADD);
    this.contentContainer.addAt(progressGlow, this.contentContainer.list.indexOf(this.progressBarBg));
    
    // Fade in progress bar after 1 second
    this.time.delayedCall(1000, () => {
      this.tweens.add({
        targets: [this.progressBarBg, this.progressBarFill, progressGlow],
        alpha: { from: 0, to: 1 },
        duration: 500,
        ease: 'Power2',
        onComplete: () => {
          // Set final alpha values
          this.progressBarBg.setAlpha(0.8);
          this.progressBarFill.setAlpha(0.9);
          progressGlow.setAlpha(0.2);
        }
      });
    });
  }

  createLoadingIndicator(width, height) {
    return;
  }

  update(time, delta) {
    if (!this.startTime) return;
    
    const elapsed = time - this.startTime;
    const remaining = Math.max(0, this.displayDuration - elapsed);
    const progress = Math.max(0, Math.min(1, remaining / this.displayDuration));
    const remainingSeconds = Math.ceil(remaining / 1000);
    
    // Update progress bar
    if (this.progressBarFill && this.progressBarBg) {
      const barWidth = this.progressBarBg.width;
      const newWidth = barWidth * progress;
      this.progressBarFill.width = newWidth;
      this.progressBarFill.x = -barWidth / 2;
      
      // Change color based on remaining time
      if (remainingSeconds <= 3) {
        this.progressBarFill.setFillStyle(0xEF4444); // Red
      } else if (remainingSeconds <= 6) {
        this.progressBarFill.setFillStyle(0xF59E0B); // Orange
      } else {
        this.progressBarFill.setFillStyle(0xF3F300); // Yellow
      }
    }
    
    // Update loading progress
    if (this.loadingBar && this.loadingText) {
      const progress = this.loadingProgress / 100;
      const barWidth = 180 * progress;
      this.loadingBar.width = barWidth;
      
      this.loadingText.setText(`Loading Assets... ${this.loadingProgress}%`);
      
      if (this.assetsLoaded) {
        this.loadingText.setText('✅ Assets Loaded');
        // Only set color if texture is ready
        if (this.loadingText.texture && this.loadingText.texture.source) {
          try {
            this.loadingText.setColor('#10B981');
          } catch (error) {
            console.warn('Loading text texture not ready for color update:', error);
          }
        }
      }
    }
    
    // Transition to GameScene when time is up and assets are loaded
    if (remaining <= 0 && this.assetsLoaded) {
      console.log('🎮 Transitioning to GameScene...');
      
      // Stop lobby music before transitioning to battle
      if (this.currentLobbyMusic && this.currentLobbyMusicKey) {
        console.log('🎵 Stopping lobby music before battle starts:', this.currentLobbyMusicKey);
        this.sound.stopByKey(this.currentLobbyMusicKey);
        this.currentLobbyMusic = null;
        this.currentLobbyMusicKey = null;
      }
      
      // Update state to 'battle' before transitioning
      sessionStateManager.updateState('battle');
      
      // Pass lobby music state to GameScene (GameScene will start fresh with battle music)
      const musicState = null; // GameScene will start its own battle music
      
      this.scene.start('GameScene', { musicState });
    }
  }

  shutdown() {
    // Stop lobby music when transitioning to battle
    if (this.currentLobbyMusic && this.currentLobbyMusicKey) {
      console.log('🎵 Stopping lobby music for battle transition:', this.currentLobbyMusicKey);
      this.sound.stopByKey(this.currentLobbyMusicKey);
      this.currentLobbyMusic = null;
      this.currentLobbyMusicKey = null;
    }
    
    // Clean up focus event listeners
    if (this.focusHandler) {
      window.removeEventListener('focus', this.focusHandler);
    }
    if (this.blurHandler) {
      window.removeEventListener('blur', this.blurHandler);
    }
    if (this.visibilityChangeHandler) {
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    
    console.log('🎬 PreBattleScene shutdown');
  }

  createDebugButton(width, height) {
    // Debug button removed from PreBattleScene - moved to GameScene
    // This was causing issues because GameScene wasn't started yet
    console.log('🔧 DEBUG: Debug button moved to GameScene');
  }

  // triggerQuickBattleEnd method moved to GameScene

  /**
   * Handle battlefield purchase
   * Called when user clicks purchase button or types "buy" command
   */
  async handleBattlefieldPurchase() {
    if (!this.battlefieldInstance || !this.battlefieldInstance.isForSale) {
      console.warn('⚠️ Battlefield not available for purchase');
      return;
    }

    try {
      // Get JWT token from window (set by React app)
      const token = window.__SW_JWT_TOKEN__ || null;
      
      if (!token) {
        showMarqueeMessage(
          '❌ Please log in to purchase a battlefield',
          { color: '#EF4444' },
          'high'
        );
        return;
      }

      const response = await fetch(`/api/battlefields/${this.battlefieldInstance.id}/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_SOULWARS_API_KEY || 'dev-key'
        },
        body: JSON.stringify({})
      });

      const data = await response.json();

      if (data.success) {
        // Update instance data
        this.battlefieldInstance = data.data;
        this.battlefieldInstance.isForSale = false;
        this.battlefieldInstance.ownerUserId = data.data.ownerUserId;

        // Show success message
        const battlefieldName = this.battlefieldInstance.themeBattlefield?.name || 'Battlefield';
        showMarqueeMessage(
          `🎉 Successfully purchased ${battlefieldName}!`,
          { color: '#10B981' },
          'high'
        );

        // Update UI to hide purchase button
        if (this.purchaseButton) {
          this.purchaseButton.setVisible(false);
        }
        if (this.purchaseText) {
          this.purchaseText.setVisible(false);
        }

        console.log('✅ Battlefield purchased successfully');
      } else {
        showMarqueeMessage(
          `❌ Purchase failed: ${data.error || 'Unknown error'}`,
          { color: '#EF4444' },
          'high'
        );
        console.error('❌ Purchase failed:', data.error);
      }
    } catch (error) {
      console.error('❌ Error purchasing battlefield:', error);
      showMarqueeMessage(
        '❌ Error purchasing battlefield. Please try again.',
        { color: '#EF4444' },
        'high'
      );
    }
  }

  /**
   * Handle "buy" command from chat
   * Called by ChatCommands.js when user types "buy" in PreBattleScene
   */
  handleShopBuyCommand(config) {
    // Check if this is a battlefield purchase context
    if (this.battlefieldInstance && this.battlefieldInstance.isForSale && !this.battlefieldInstance.ownerUserId) {
      this.handleBattlefieldPurchase();
      return true;
    }
    
    // Not a battlefield purchase, return false to let other handlers try
    return false;
  }
}
